import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import Select from "react-select"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import { useSelector } from "react-redux"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomCheckbox from "../../../components/OnboardingComponents/CustomCheckbox"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import {
  ChevronDropdown,
  customStyles,
} from "../../../components/OnboardingComponents/RoleSelect"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  getCityAdminCitySelectOptions,
  isCityAdminUser,
} from "../../../helpers/cityPortalRoles"
import StaffCityMultiSelect from "../Staff/StaffCityMultiSelect"
import {
  REWARD_MAX_MEDIA_BYTES,
  REWARD_MAX_MEDIAS,
  REWARD_MEDIA_ACCEPT,
  REWARD_STATUS_FILTER_OPTIONS,
  buildRewardFormData,
  getRewardMediaTypeLabel,
  isAcceptedRewardMediaFile,
  isRewardMediaImage,
  mapCitySelectOptions,
  mapRewardDetail,
  mapRewardTypeOptions,
  toRewardFlag,
} from "./adminRewardsData"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const emptyValues = {
  name: "",
  type: "",
  isAllCities: true,
  cityIds: [],
  description: "",
  status: "active",
  isOneTime: false,
  totalQuantity: "",
  previews: [],
  initialMediaIds: [],
  removedMediaIds: [],
}

const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Treasure name is required"),
  type: yup.string().trim().required("Treasure type is required"),
  cityIds: yup.array().when("isAllCities", {
    is: false,
    then: schema =>
      schema
        .of(yup.string())
        .min(1, "Select at least one city")
        .required("Select at least one city"),
    otherwise: schema => schema.notRequired(),
  }),
  status: yup
    .string()
    .oneOf(["active", "draft"], "Select active or draft")
    .required("Status is required"),
  description: yup.string().trim().required("Instruction is required"),
  totalQuantity: yup.number().when("isOneTime", {
    is: true,
    then: schema => schema.notRequired().nullable(),
    otherwise: schema =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value,
        )
        .typeError("Quantity must be a number")
        .integer("Quantity must be a whole number")
        .min(0, "Quantity cannot be negative")
        .required("Total quantity is required"),
  }),
  previews: yup
    .array()
    .max(REWARD_MAX_MEDIAS, `You can upload up to ${REWARD_MAX_MEDIAS} files`),
})

const findOption = (options, value) =>
  options.find(option => option.value === value) || null

const revokePreviewUrls = (previews = []) => {
  ;(Array.isArray(previews) ? previews : []).forEach(item => {
    if (item?.url?.startsWith("blob:")) URL.revokeObjectURL(item.url)
  })
}

const createFilePreview = file => {
  const mimeType = String(file.type || "").toLowerCase()
  const isImage = isRewardMediaImage({ mimeType, name: file.name, file })
  return {
    id: `local-${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
    url: isImage ? URL.createObjectURL(file) : "",
    name: file.name,
    file,
    mimeType,
    isExisting: false,
    mediaId: null,
  }
}

/**
 * Same custom selector used on Staff create/edit (chevron always visible,
 * menu portaled above modal).
 */
const RewardFormSelect = ({
  id,
  label,
  required = false,
  options,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  isLoading = false,
  isSearchable = true,
}) => {
  const hasError = Boolean(error)
  const selectStyles = customStyles(hasError)

  return (
    <div className={`amok-field ${hasError ? "amok-field--error" : ""}`.trim()}>
      <label className="amok-field__label" htmlFor={id}>
        {label}
        {required ? <span className="amok-field__required"> *</span> : null}
      </label>
      <Select
        inputId={id}
        className={`amok-role-select ${hasError ? "amok-role-select--error" : ""}`}
        classNamePrefix="amok-role-select"
        options={options}
        value={findOption(options, value)}
        onChange={option => onChange(option?.value || "")}
        onBlur={onBlur}
        placeholder={placeholder}
        isLoading={isLoading}
        isSearchable={isSearchable}
        isClearable={false}
        openMenuOnFocus
        openMenuOnClick
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        components={{
          DropdownIndicator: ChevronDropdown,
          IndicatorSeparator: null,
        }}
        styles={{
          ...selectStyles,
          menuPortal: base => ({ ...base, zIndex: 10060 }),
        }}
        noOptionsMessage={() => "No options found"}
      />
      {hasError ? (
        <p className="amok-field__message amok-field__message--error">{error}</p>
      ) : null}
    </div>
  )
}

RewardFormSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
  isSearchable: PropTypes.bool,
}

RewardFormSelect.defaultProps = {
  required: false,
  onBlur: undefined,
  error: "",
  placeholder: "Select…",
  isLoading: false,
  isSearchable: true,
}

/**
 * Add / Edit Reward modal — wired to admin rewards create/update APIs.
 */
const AddRewardModal = ({ isOpen, onClose, onSuccess, rewardId }) => {
  const isEdit = Boolean(rewardId)
  const currentUser = useSelector(state => state.auth.currentUser)
  const isCityAdmin = isCityAdminUser(currentUser)
  const cityAdminOptions = useMemo(
    () => getCityAdminCitySelectOptions(currentUser),
    [currentUser],
  )
  const fileInputRef = useRef(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [typeOptions, setTypeOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [initialValues, setInitialValues] = useState(emptyValues)

  useEffect(() => {
    if (!isOpen) return undefined

    let cancelled = false

    const loadOptions = async () => {
      setOptionsLoading(true)
      setCitiesLoading(true)
      try {
        const typesPromise = get(endpoints.getAdminRewardTypes)
        const citiesPromise = isCityAdmin
          ? Promise.resolve(null)
          : get(endpoints.getCites, { skipErrorToast: true })

        const [typesResponse, citiesResponse] = await Promise.all([
          typesPromise,
          citiesPromise,
        ])

        if (cancelled) return

        if (typesResponse?.data?.success) {
          setTypeOptions(mapRewardTypeOptions(typesResponse.data.result || []))
        } else {
          toastApiError(typesResponse, "Failed to load treasure types")
        }

        if (isCityAdmin) {
          setCityOptions(cityAdminOptions)
        } else if (citiesResponse?.data?.success) {
          const result = citiesResponse.data.result
          const list = Array.isArray(result)
            ? result
            : Array.isArray(result?.data)
              ? result.data
              : []
          setCityOptions(mapCitySelectOptions(list))
        } else {
          setCityOptions([])
        }
      } catch (err) {
        if (!cancelled) toastApiError(err, "Failed to load form options")
      } finally {
        if (!cancelled) {
          setOptionsLoading(false)
          setCitiesLoading(false)
        }
      }
    }

    const loadDetail = async () => {
      if (!rewardId) {
        setInitialValues(
          isCityAdmin
            ? { ...emptyValues, isAllCities: false }
            : emptyValues,
        )
        return
      }

      setLoadingDetail(true)
      try {
        const response = await get(endpoints.getAdminRewardById(rewardId), {
          params: { "with[]": ["creator", "medias", "cities"] },
        })
        if (cancelled) return
        if (response?.data?.success) {
          const detail = mapRewardDetail(response.data.result || {})
          const existing = (detail.medias || []).slice(0, REWARD_MAX_MEDIAS).map(
            media => ({
              id: media.id,
              url: media.url || media.thumbnailUrl || "",
              name: media.name || "Treasure media",
              file: null,
              mimeType: media.mimeType || "",
              isExisting: true,
              mediaId: media.id,
            }),
          )
          const allowedCityIds = new Set(
            cityAdminOptions.map(option => String(option.value)),
          )
          const detailCityIds = (detail.cityIds || []).map(String)
          const scopedCityIds = isCityAdmin
            ? detailCityIds.filter(id => allowedCityIds.has(id))
            : detailCityIds
          const forceCityScoped =
            isCityAdmin &&
            (Boolean(detail.isAllCities) || scopedCityIds.length === 0)

          setInitialValues({
            name: detail.name || "",
            type: detail.type || "",
            isAllCities: isCityAdmin
              ? false
              : Boolean(detail.isAllCities),
            cityIds: isCityAdmin
              ? forceCityScoped && scopedCityIds.length === 0
                ? cityAdminOptions.map(option => String(option.value))
                : scopedCityIds
              : detail.isAllCities
                ? []
                : detailCityIds,
            description: detail.description || "",
            status: detail.status === "draft" ? "draft" : "active",
            isOneTime: toRewardFlag(detail.raw?.is_one_time ?? detail.isOneTime),
            totalQuantity: String(
              Number.isFinite(detail.totalQuantity) ? detail.totalQuantity : 0,
            ),
            previews: existing,
            initialMediaIds: (detail.medias || [])
              .map(media => media.id)
              .filter(Boolean),
            removedMediaIds: [],
          })
        } else {
          toastApiError(response, "Failed to load treasure")
          onClose()
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load treasure")
          onClose()
        }
      } finally {
        if (!cancelled) setLoadingDetail(false)
      }
    }

    loadOptions()
    loadDetail()

    return () => {
      cancelled = true
    }
  }, [cityAdminOptions, isCityAdmin, isOpen, onClose, rewardId])

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError, setFieldTouched }) => {
    try {
      const formData = buildRewardFormData({ values, isEdit })
      const response = isEdit
        ? await post(endpoints.updateAdminReward(rewardId), formData, {
            skipErrorToast: true,
          })
        : await post(endpoints.createAdminReward, formData, {
            skipErrorToast: true,
          })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        reportFormApiError(
          response,
          isEdit ? "Failed to update treasure" : "Failed to create treasure",
          { setFieldError, setFieldTouched },
          {
            total_quantity: "totalQuantity",
            city_ids: "cityIds",
            "city_ids.0": "cityIds",
          },
        )
        return
      }

      toast.success(
        response.data?.message ||
          (isEdit ? "Treasure updated successfully." : "Treasure saved successfully."),
        TOAST_OPTIONS,
      )
      revokePreviewUrls(values.previews)
      resetForm()
      onSuccess?.()
      onClose()
    } catch (err) {
      reportFormApiError(
        err,
        isEdit ? "Failed to update treasure" : "Failed to create treasure",
        { setFieldError, setFieldTouched },
        {
          total_quantity: "totalQuantity",
          city_ids: "cityIds",
          "city_ids.0": "cityIds",
        },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password admin-reward-modal"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body edit-profile__modal-body">
        <Formik
          key={
            isOpen
              ? `reward-form-${rewardId || "new"}-${loadingDetail ? "loading" : "ready"}`
              : "reward-form-closed"
          }
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            setFieldError,
            isSubmitting,
          }) => {
            const resolvedTypeOptions = (() => {
              if (!values.type) return typeOptions
              if (typeOptions.some(option => option.value === values.type)) {
                return typeOptions
              }
              return [
                { value: values.type, label: values.type },
                ...typeOptions,
              ]
            })()

            const acceptFiles = fileList => {
              setFieldTouched("previews", true, false)
              const incoming = Array.from(fileList || []).filter(
                isAcceptedRewardMediaFile,
              )

              if (incoming.length === 0) {
                setFieldError(
                  "previews",
                  "Please upload PNG, JPG, WEBP, SVG, PDF, or CSV files",
                )
                return
              }

              const oversized = incoming.find(
                file => file.size > REWARD_MAX_MEDIA_BYTES,
              )
              if (oversized) {
                setFieldError("previews", "Each file must be 5MB or smaller")
                return
              }

              const remainingSlots = Math.max(
                0,
                REWARD_MAX_MEDIAS - values.previews.length,
              )
              if (remainingSlots === 0) {
                setFieldError(
                  "previews",
                  `You can upload up to ${REWARD_MAX_MEDIAS} files`,
                )
                return
              }

              const nextFiles = incoming
                .slice(0, remainingSlots)
                .map(createFilePreview)
              setFieldValue("previews", [...values.previews, ...nextFiles])
              setFieldError("previews", undefined)
            }

            const removePreview = previewId => {
              const target = values.previews.find(item => item.id === previewId)
              const nextRemoved = [...(values.removedMediaIds || [])]
              if (target?.isExisting && target?.mediaId) {
                nextRemoved.push(target.mediaId)
              }
              if (target?.url?.startsWith("blob:")) {
                URL.revokeObjectURL(target.url)
              }
              setFieldValue(
                "previews",
                values.previews.filter(item => item.id !== previewId),
              )
              setFieldValue("removedMediaIds", [...new Set(nextRemoved)])
              setFieldTouched("previews", true, false)
            }

            return (
              <Form className="edit-profile__modal-form">
                <div className="edit-profile__modal-main">
                  <div className="admin-reward-modal__intro">
                    <h3 className="edit-profile__modal-title">
                      {isEdit ? "Edit Treasure" : "Add Treasure"}
                    </h3>
                    <p className="admin-reward-modal__subtitle">
                      {isEdit
                        ? "Update treasure details, quantity, and media."
                        : "Create a new treasure that members can claim."}
                    </p>
                  </div>

                  {loadingDetail ? (
                    <div
                      className="admin-reward-modal__form-skeleton"
                      aria-busy="true"
                    >
                      <div className="admin-rewards__skeleton admin-rewards__skeleton--field" />
                      <div className="admin-rewards__skeleton admin-rewards__skeleton--field" />
                      <div className="admin-rewards__skeleton admin-rewards__skeleton--field" />
                      <div className="admin-rewards__skeleton admin-rewards__skeleton--textarea" />
                      <div className="admin-rewards__skeleton admin-rewards__skeleton--media" />
                    </div>
                  ) : (
                    <div className="edit-profile__modal-fields">
                      <CustomInput
                        label="Treasure Name"
                        name="name"
                        required
                        placeholder="Enter treasure name (e.g., Event Ticket)"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("name", true)}
                        error={touched.name && errors.name}
                      />

                      <RewardFormSelect
                        id="reward-type"
                        label="Treasure Type"
                        required
                        options={resolvedTypeOptions}
                        value={values.type}
                        onChange={next => {
                          setFieldValue("type", next)
                          setFieldTouched("type", true, false)
                        }}
                        onBlur={() => setFieldTouched("type", true)}
                        error={touched.type && errors.type}
                        placeholder="Select treasure type"
                        isLoading={optionsLoading}
                        isSearchable
                      />

                      <RewardFormSelect
                        id="reward-status"
                        label="Status"
                        required
                        options={REWARD_STATUS_FILTER_OPTIONS}
                        value={values.status || "active"}
                        onChange={next => {
                          setFieldValue("status", next || "active")
                          setFieldTouched("status", true, false)
                        }}
                        onBlur={() => setFieldTouched("status", true)}
                        error={touched.status && errors.status}
                        placeholder="Select status"
                        isSearchable={false}
                      />

                      <div className="admin-reward-modal__checks">
                        {!isCityAdmin ? (
                          <CustomCheckbox
                            checked={values.isAllCities}
                            onChange={event => {
                              const next = Boolean(event.target.checked)
                              setFieldValue("isAllCities", next)
                              if (next) {
                                setFieldValue("cityIds", [])
                                setFieldError("cityIds", undefined)
                              }
                            }}
                            label="All cities"
                            checkIconSrc={ICONS.check}
                          />
                        ) : null}
                        <CustomCheckbox
                          checked={values.isOneTime}
                          onChange={event => {
                            const next = Boolean(event.target.checked)
                            setFieldValue("isOneTime", next)
                            if (next) {
                              setFieldValue("totalQuantity", "")
                              setFieldError("totalQuantity", undefined)
                            }
                          }}
                          label="One-time claim only"
                          checkIconSrc={ICONS.check}
                        />
                      </div>

                      {!values.isAllCities || isCityAdmin ? (
                        <StaffCityMultiSelect
                          id="reward-cities"
                          label="Cities"
                          options={cityOptions}
                          value={values.cityIds}
                          onChange={next => {
                            setFieldValue("cityIds", next)
                            setFieldTouched("cityIds", true, false)
                          }}
                          onBlur={() => setFieldTouched("cityIds", true)}
                          error={touched.cityIds && errors.cityIds}
                          placeholder={
                            citiesLoading
                              ? "Loading cities..."
                              : "Search cities..."
                          }
                          isLoading={citiesLoading}
                        />
                      ) : null}

                      {!values.isOneTime ? (
                        <CustomInput
                          label="Total Quantity"
                          name="totalQuantity"
                          required
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter available quantity"
                          value={values.totalQuantity}
                          onChange={event => {
                            const next = event.target.value
                            if (next === "" || /^\d+$/.test(next)) {
                              setFieldValue("totalQuantity", next)
                            }
                          }}
                          onBlur={() => setFieldTouched("totalQuantity", true)}
                          error={touched.totalQuantity && errors.totalQuantity}
                        />
                      ) : null}

                      <CustomInput
                        label="Instruction"
                        name="description"
                        required
                        textarea
                        rows={4}
                        placeholder="Enter treasure instruction"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("description", true)}
                        error={touched.description && errors.description}
                      />

                      <div className="admin-reward-modal__upload">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={REWARD_MEDIA_ACCEPT}
                          multiple
                          className="admin-reward-modal__file-input"
                          onChange={event => {
                            acceptFiles(event.target.files)
                            event.target.value = ""
                          }}
                        />

                        {values.previews.length > 0 ? (
                          <div className="admin-reward-modal__previews">
                            {values.previews.map(preview => {
                              const showImage = isRewardMediaImage(preview)
                              return (
                                <div
                                  key={preview.id}
                                  className="admin-reward-modal__preview-card"
                                >
                                  {showImage && preview.url ? (
                                    <img
                                      src={preview.url}
                                      alt=""
                                      className="admin-reward-modal__preview-image"
                                    />
                                  ) : (
                                    <div
                                      className="admin-reward-modal__preview-file"
                                      title={preview.name}
                                    >
                                      <span className="admin-reward-modal__preview-file-type">
                                        {getRewardMediaTypeLabel(preview)}
                                      </span>
                                      <span className="admin-reward-modal__preview-file-name">
                                        {preview.name}
                                      </span>
                                    </div>
                                  )}
                                  <CustomButton
                                    variant="danger"
                                    size="sm"
                                    type="button"
                                    onClick={() => removePreview(preview.id)}
                                  >
                                    Remove
                                  </CustomButton>
                                </div>
                              )
                            })}
                            {values.previews.length < REWARD_MAX_MEDIAS ? (
                              <button
                                type="button"
                                className="admin-reward-modal__upload-trigger admin-reward-modal__upload-trigger--compact"
                                onClick={() => fileInputRef.current?.click()}
                              >
                                <span className="admin-reward-modal__upload-icon-wrap">
                                  <img
                                    src={ICONS.callUploadExport}
                                    alt=""
                                    className="admin-reward-modal__upload-icon"
                                  />
                                </span>
                                <span className="admin-reward-modal__upload-title">
                                  Add another file
                                </span>
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="admin-reward-modal__upload-trigger"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <span className="admin-reward-modal__upload-icon-wrap">
                              <img
                                src={ICONS.callUploadExport}
                                alt=""
                                className="admin-reward-modal__upload-icon"
                              />
                            </span>
                            <span className="admin-reward-modal__upload-title">
                              Upload treasure media
                            </span>
                            <span className="admin-reward-modal__upload-hint">
                              PNG, JPG, WEBP, SVG, PDF or CSV. Max{" "}
                              {REWARD_MAX_MEDIAS} files, 5MB each.
                            </span>
                          </button>
                        )}
                        {touched.previews && errors.previews ? (
                          <p className="amok-field__message amok-field__message--error">
                            {errors.previews}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                <div className="edit-profile__modal-actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="edit-profile__modal-cancel"
                    onClick={onClose}
                    disabled={isSubmitting || loadingDetail}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="edit-profile__modal-submit"
                    loading={isSubmitting}
                    disabled={
                      isSubmitting ||
                      loadingDetail ||
                      optionsLoading ||
                      citiesLoading
                    }
                  >
                    Save Treasure
                  </CustomButton>
                </div>
              </Form>
            )
          }}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

AddRewardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  rewardId: PropTypes.string,
}

AddRewardModal.defaultProps = {
  onSuccess: undefined,
  rewardId: null,
}

export default AddRewardModal
